/**
 * BusinessOverviewTab — Overview tab content for Business Workspace.
 *
 * Shows business summary, scope, "why now" trigger, and quick action cards
 * that navigate into creative generation, offer building, etc.
 */
import React from 'react';
import {
  Star, MapPin, Globe, Sparkles, DollarSign,
  FileText, Video, Clock3, AlertCircle, BarChart3, CheckCircle2,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

export default function BusinessOverviewTab({
  business,
  businessProfile,
  businessEnrichment,
  scope,
  lead,
  onGenerateCreative,
  onBuildOffer,
  onViewProposals,
  onRetryBusinessProfileEnrichment,
  creativesCount = 0,
  proposalsCount = 0,
  campaignCount = 0,
  activities = [],
  blockerSummary,
  latestCreative,
  latestProposal,
}) {
  const bp = { ...(businessProfile || {}), ...(business || {}) };
  const enrichmentStatus = businessEnrichment?.enrichmentStatus || 'ready';
  const creativeGenerationBlocked = enrichmentStatus !== 'ready';
  const daysInWorkspace = bp.createdAt
    ? Math.max(1, Math.round((Date.now() - new Date(bp.createdAt).getTime()) / (1000 * 60 * 60 * 24)))
    : null;

  let workspaceState = {
    label: 'Business qualified',
    description: 'Choose the easiest path forward: make a sample ad or go straight into offer shaping.',
    tone: 'slate',
  };

  if (campaignCount > 0) {
    workspaceState = {
      label: 'Campaign live',
      description: 'This account has a live campaign. Use reporting and follow-up to support retention or renewal.',
      tone: 'emerald',
    };
  } else if (latestProposal?.status === 'accepted') {
    workspaceState = {
      label: 'Accepted and ready for launch handoff',
      description: 'Commercial approval is complete. Payment and launch setup are the only remaining steps.',
      tone: 'blue',
    };
  } else if (latestProposal?.status === 'sent') {
    workspaceState = {
      label: 'Awaiting buyer response',
      description: 'A proposal is already in front of the buyer. The next job is follow-up, not more configuration.',
      tone: 'amber',
    };
  } else if (latestProposal) {
    workspaceState = {
      label: 'Proposal in progress',
      description: 'You have commercial work underway. Finish the proposal and move it to buyer review.',
      tone: 'blue',
    };
  } else if (creativesCount > 0) {
    workspaceState = {
      label: 'Pitch asset ready',
      description: 'Creative exists. Turn that momentum into an offer and proposal while the opportunity is still fresh.',
      tone: 'coral',
    };
  }

  let primaryAction = {
    label: creativesCount > 0 ? 'Build offer' : 'Generate creative',
    description: creativesCount > 0
      ? 'Use the existing creative to package an offer the buyer can react to.'
      : 'Make a lightweight sample ad to make the pitch more concrete.',
    onClick: creativesCount > 0 ? onBuildOffer : onGenerateCreative,
    icon: creativesCount > 0 ? DollarSign : Video,
  };

  if (latestProposal?.status === 'draft' || latestProposal?.status === 'changes_requested') {
    primaryAction = {
      label: 'Finish proposal',
      description: 'There is already a draft in progress. Tighten the commercial package and send it.',
      onClick: onViewProposals,
      icon: FileText,
    };
  } else if (latestProposal?.status === 'sent') {
    primaryAction = {
      label: 'Manage proposal',
      description: 'Review proposal status, confirm follow-up timing, and keep the buyer moving toward close.',
      onClick: onViewProposals,
      icon: FileText,
    };
  }

  const blockerList = blockerSummary?.blockers?.length
    ? blockerSummary.blockers.map((blocker) => blocker.detail)
    : [];

  const recentMoments = activities.slice(0, 3);
  const stateToneClasses = {
    slate: 'border-slate-200 bg-slate-50 text-slate-600',
    coral: 'border-coral/10 bg-coral/[0.03] text-coral/80',
    amber: 'border-amber-100 bg-amber-50/50 text-amber-600',
    blue: 'border-blue-100 bg-blue-50/50 text-blue-600',
    emerald: 'border-emerald-100 bg-emerald-50/50 text-emerald-600',
  };
  // Hide the secondary "Build offer" button until at least one creative
  // exists. The intended flow is creative → offer; offering this as a
  // co-primary on a fresh workspace pulls non-expert sellers off the path.
  const showBuildOfferAction = primaryAction.label !== 'Build offer' && creativesCount > 0;
  const showViewProposalsAction = proposalsCount > 0 && primaryAction.label !== 'Manage proposal' && primaryAction.label !== 'Finish proposal';

  return (
    <div className="grid gap-6 xl:grid-cols-[1.4fr_0.9fr]">
      <div className="space-y-6">
        <Card className="gap-0 overflow-hidden border-border bg-card shadow-sm">
          <CardContent className="p-6">
            <div className="flex flex-col gap-6 xl:flex-row xl:items-start xl:justify-between">
              <div className="min-w-0 max-w-2xl">
                <Badge variant="outline" className={`rounded-full border px-3 py-1 text-[11px] font-medium uppercase tracking-[0.12em] ${stateToneClasses[workspaceState.tone]}`}>
                  {workspaceState.label}
                </Badge>
                <h2 className="mt-4 break-words text-2xl font-bold tracking-tight text-slate-950 sm:text-3xl">
                  {primaryAction.label}
                </h2>
                <p className="mt-2 text-sm leading-6 text-slate-600">{primaryAction.description}</p>
                <div className="mt-5 flex flex-wrap items-center gap-3">
                  <Button
                    onClick={primaryAction.onClick}
                    disabled={creativeGenerationBlocked && primaryAction.label === 'Generate creative'}
                    className="h-11 rounded-xl bg-coral text-white hover:bg-coral/90 shadow-sm"
                  >
                    <primaryAction.icon className="h-4 w-4" />
                    {primaryAction.label}
                  </Button>
                  {showBuildOfferAction && (
                    <Button variant="outline" onClick={onBuildOffer} className="rounded-xl bg-white">
                      <DollarSign className="h-4 w-4" />
                      Build offer
                    </Button>
                  )}
                  {showViewProposalsAction && (
                    <Button variant="outline" onClick={onViewProposals} className="rounded-xl bg-white">
                      <FileText className="h-4 w-4" />
                      View proposals
                    </Button>
                  )}
                  {creativeGenerationBlocked && enrichmentStatus === 'failed' && onRetryBusinessProfileEnrichment ? (
                    <Button variant="outline" onClick={onRetryBusinessProfileEnrichment} className="rounded-xl bg-white">
                      Retry profile build
                    </Button>
                  ) : null}
                </div>
                {creativeGenerationBlocked ? (
                  <p className="mt-3 text-sm text-slate-500">
                    {enrichmentStatus === 'pending'
                      ? 'Creative generation unlocks once the business profile finishes building.'
                      : 'Creative generation stays blocked until the business profile build succeeds.'}
                  </p>
                ) : null}
              </div>

              <div className="flex items-center gap-5 xl:w-auto xl:self-center">
                <div className="text-center">
                  <p className="text-2xl font-semibold text-slate-950">{creativesCount}</p>
                  <p className="flex items-center gap-1 text-[11px] font-medium text-slate-500">
                    <Video className="h-3 w-3 text-coral" />
                    Creative
                  </p>
                </div>
                <div className="h-8 w-px bg-slate-200" />
                <div className="text-center">
                  <p className="text-2xl font-semibold text-slate-950">{proposalsCount}</p>
                  <p className="flex items-center gap-1 text-[11px] font-medium text-slate-500">
                    <FileText className="h-3 w-3 text-teal" />
                    Proposals
                  </p>
                </div>
                <div className="h-8 w-px bg-slate-200" />
                <div className="text-center">
                  <p className="text-2xl font-semibold text-slate-950">{campaignCount}</p>
                  <p className="flex items-center gap-1 text-[11px] font-medium text-slate-500">
                    <BarChart3 className="h-3 w-3 text-blue-500" />
                    Campaigns
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {lead?.triggerEvent && (
          <Card className="border-amber-100 bg-amber-50/30 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-50">
                  <Sparkles className="h-4 w-4 text-amber-500" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-slate-950">Why now</p>
                  <p className="mt-1 text-sm text-slate-700">{lead.triggerEvent}</p>
                  {lead.sellerAngle && (
                    <p className="mt-2 text-sm italic text-slate-500">&quot;{lead.sellerAngle}&quot;</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-5">
            <h3 className="text-base font-semibold text-slate-950">Recent activity</h3>

            <div className="mt-4 space-y-4">
              {recentMoments.length > 0 ? recentMoments.map((activity) => (
                <div key={activity.id} className="flex gap-3">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-teal" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-950">{activity.label}</p>
                    <p className="mt-0.5 text-sm text-slate-500">{activity.detail}</p>
                    <p className="mt-1 text-xs text-slate-400">
                      {new Date(activity.timestamp).toLocaleString([], {
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>
                </div>
              )) : (
                <p className="text-sm text-slate-500">
                  No tracked activity yet.
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="space-y-6">
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-5">
            <h3 className="text-base font-semibold text-slate-950">Business snapshot</h3>
            <div className="mt-5 space-y-4">
              <div className="flex items-start gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-coral/8">
                  <span className="text-lg font-semibold text-coral">{bp.name?.charAt(0) || '?'}</span>
                </div>
                <div className="min-w-0">
                  <p className="break-words text-sm font-semibold text-slate-950">{bp.name || 'Unknown business'}</p>
                  <p className="mt-1 break-words text-sm text-slate-500">{bp.category || 'Business profile'}</p>
                  {scope && (
                    <Badge variant="outline" className="mt-2 rounded-full bg-slate-50 text-xs text-slate-600">
                      {scope === 'brand_wide' ? 'Brand-wide scope' : 'Single-location scope'}
                    </Badge>
                  )}
                </div>
              </div>

              <div className="space-y-3 text-sm text-slate-600">
                {bp.address && (
                  <div className="flex items-start gap-2">
                    <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                    <span className="min-w-0 break-words">{bp.address}</span>
                  </div>
                )}
                {bp.website_url && (
                  <div className="flex items-start gap-2">
                    <Globe className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                    <span className="min-w-0 break-all">{bp.website_url.replace(/^https?:\/\//, '').replace(/\/$/, '')}</span>
                  </div>
                )}
                {(bp.rating > 0 || bp.reviews > 0) && (
                  <div className="flex items-center gap-2">
                    <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
                    <span>
                      {bp.rating || 'N/A'}
                      {bp.reviews > 0 ? ` (${bp.reviews} reviews)` : ''}
                    </span>
                  </div>
                )}
                {daysInWorkspace && (
                  <div className="flex items-center gap-2">
                    <Clock3 className="h-4 w-4 text-slate-400" />
                    <span>Added to workspace {daysInWorkspace} day{daysInWorkspace !== 1 ? 's' : ''} ago</span>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-5">
            <h3 className="text-base font-semibold text-slate-950">Readiness status</h3>
            <div className={`mt-4 rounded-2xl border p-4 ${stateToneClasses[workspaceState.tone]}`}>
              <p className="text-sm font-semibold">{workspaceState.label}</p>
              <p className="mt-1 text-sm">{workspaceState.description}</p>
            </div>

            <div className="mt-4 space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-slate-950">
                    {latestCreative ? (latestCreative.displayName || latestCreative.creativeName || latestCreative.objective || 'Spec ad') : 'No creative'}
                  </p>
                  <p className="mt-0.5 text-sm text-slate-500">
                    {latestCreative ? `Creative · ${latestCreative.status || 'draft'}` : 'No pitch asset yet'}
                  </p>
                </div>
                <Video className="h-4 w-4 shrink-0 text-coral" />
              </div>
              <div className="h-px bg-slate-100" />
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-slate-950">
                    {latestProposal ? (latestProposal.packageName || latestProposal.name || 'Proposal draft') : 'No proposal'}
                  </p>
                  <p className="mt-0.5 text-sm text-slate-500">
                    {latestProposal ? `Proposal · ${String(latestProposal.status || 'draft').replace(/_/g, ' ')}` : 'No commercial package yet'}
                  </p>
                </div>
                <FileText className="h-4 w-4 shrink-0 text-teal" />
              </div>
              {blockerSummary && (
                <>
                  <div className="h-px bg-slate-100" />
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium text-slate-950">
                        {blockerSummary.allReady ? 'Ready to launch' : `${blockerSummary.blockers.length} blocker${blockerSummary.blockers.length !== 1 ? 's' : ''}`}
                      </p>
                      <p className="mt-0.5 text-sm text-slate-500">Launch readiness</p>
                    </div>
                    <BarChart3 className="h-4 w-4 shrink-0 text-blue-500" />
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-amber-500" />
              <h3 className="text-base font-semibold text-slate-950">Current blockers</h3>
            </div>
            <div className="mt-4 space-y-3">
              {blockerList.length > 0 ? blockerList.map((blocker) => (
                <div key={blocker} className="rounded-2xl border border-amber-100 bg-amber-50/50 p-4 text-sm text-amber-700">
                  {blocker}
                </div>
              )) : (
                <div className="rounded-2xl border border-emerald-100 bg-emerald-50/30 p-4 text-sm text-emerald-700">
                  No major blockers are visible from the current workspace state.
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
